// v2
// Path: frontend/src/components/QuickBooksSync/IntegrationSettingsModal.jsx
// Inputs: token, onClose, onSettingsUpdated, onOpenStandardMappings
// Outputs: React JSX modal allowing configuration of multi-system entity-to-root-table defaults and QuickBooks destinations
// Summary: Environmental settings interface managing default root table names, multi-system source declarations, and target QuickBooks entities stored in Sync.EntityTableDefaults.

import React, { useState, useEffect } from 'react';


// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const IntegrationSettingsModal = ({ token, onClose, onSettingsUpdated, onOpenStandardMappings }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [defaults, setDefaults] = useState([]);
    const [availableSystems, setAvailableSystems] = useState(['Passport']);
    const [selectedSystemFilter, setSelectedSystemFilter] = useState('ALL');
    const [editingItem, setEditingItem] = useState(null);
    const [customSystemMode, setCustomSystemMode] = useState(false);
    const [form, setForm] = useState({
        sourceSystem: 'Passport',
        entityType: '',
        defaultRootTable: '',
        defaultQBEntity: 'Customer',
        description: '',
        isActive: true
    });
    const [message, setMessage] = useState(null);

    const qbEntities = [
        'Customer', 'Vendor', 'Item', 'Account', 'Invoice', 'Bill', 'Payment', 'JournalEntry', 'PurchaseOrder'
    ];

    const fetchDefaults = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/sync/settings/entity-defaults', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await unbox(res);
                if (Array.isArray(data)) {
                    setDefaults(data);
                } else if (data && Array.isArray(data.defaults)) {
                    setDefaults(data.defaults);
                    if (data.availableSystems) {
                        setAvailableSystems(data.availableSystems);
                    }
                } else {
                    setDefaults([]);
                }
            }
        } catch (err) {
            console.error('[IntegrationSettingsModal] Error loading defaults:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDefaults();
    }, [token]);

    const handleEdit = (item) => {
        setEditingItem(item);
        setCustomSystemMode(false);
        setForm({
            sourceSystem: item.SourceSystem || 'Passport',
            entityType: item.EntityType,
            defaultRootTable: item.DefaultRootTable,
            defaultQBEntity: item.DefaultQBEntity,
            description: item.Description || '',
            isActive: !!item.IsActive
        });
    };

    const handleNew = () => {
        setEditingItem('NEW');
        setCustomSystemMode(false);
        setForm({
            sourceSystem: selectedSystemFilter !== 'ALL' ? selectedSystemFilter : 'Passport',
            entityType: '',
            defaultRootTable: '',
            defaultQBEntity: 'Customer',
            description: '',
            isActive: true
        });
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
        setCustomSystemMode(false);
        setMessage(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            const res = await fetch('/api/sync/settings/entity-defaults', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(form)
            });
            const data = await unbox(res);
            if (res.ok) {
                setMessage({ type: 'success', text: data.message || 'Saved successfully!' });
                setEditingItem(null);
                setCustomSystemMode(false);
                await fetchDefaults();
                if (onSettingsUpdated) onSettingsUpdated();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to save entity default.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item) => {
        if (!(await useSyncStore.getState().showConfirm(`Are you sure you want to remove default for ${item.SourceSystem} - ${item.EntityType}?`))) return;
        try {
            const res = await fetch(`/api/sync/settings/entity-defaults/${item.EntityDefaultID}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setMessage({ type: 'success', text: 'Entity default removed successfully.' });
                await fetchDefaults();
                if (onSettingsUpdated) onSettingsUpdated();
            } else {
                const data = await unbox(res);
                setMessage({ type: 'error', text: data.error || 'Failed to delete entity default.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const filteredDefaults = defaults.filter(item => {
        if (selectedSystemFilter === 'ALL') return true;
        return (item.SourceSystem || 'Passport').toLowerCase() === selectedSystemFilter.toLowerCase();
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-850">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined text-[24px]">tune</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-white tracking-wide">
                                    Integration Environmental Settings
                                </h3>
                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                    Multi-System Ready
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">
                                Configure database-driven root table names, multi-system sources (Passport, CSV, custom), and target QuickBooks entities.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {onOpenStandardMappings && (
                            <button
                                onClick={() => { onClose(); onOpenStandardMappings(); }}
                                className="px-3 py-1.5 bg-indigo-600/80 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border border-indigo-500/40"
                                title="Open Standard Field Mappings Preset Manager"
                            >
                                <span className="material-symbols-outlined text-[15px]">schema</span>
                                Standard Field Mappings
                            </button>
                        )}
                        <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                {/* Explanation Banner */}
                <div className="px-6 py-3 bg-blue-950/40 border-b border-blue-900/40 text-xs text-blue-200 flex items-start gap-2.5">
                    <span className="material-symbols-outlined text-[18px] text-blue-400 shrink-0 mt-0.5">info</span>
                    <div className="space-y-1">
                        <div>
                            <strong>Multi-System Architecture:</strong> Configure root ERP tables and target QuickBooks Online entities for Passport or other future source systems.
                        </div>
                        <div className="text-blue-300/80">
                            <strong>Passport Company Suffix Convention:</strong> For Passport databases, the last 2 digits of the database name (e.g. <code>pbsdata00</code> &rarr; <code>00</code>) are dynamically appended to the Root Table (e.g. <code>CUSFIL</code> + <code>00</code> = <code>CUSFIL00</code>).
                        </div>
                    </div>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {message && (
                        <div className={`p-3 rounded-lg text-xs border flex items-center justify-between gap-2 ${message.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300' : 'bg-red-950/60 border-red-800 text-red-300'}`}>
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-[16px]">
                                    {message.type === 'success' ? 'check_circle' : 'error'}
                                </span>
                                <span>{message.text}</span>
                            </div>
                            <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white text-xs">
                                &times;
                            </button>
                        </div>
                    )}

                    {/* Source System Filter Tabs & Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
                        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px]">device_hub</span>
                                Source System:
                            </span>
                            <button
                                onClick={() => setSelectedSystemFilter('ALL')}
                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${selectedSystemFilter === 'ALL' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                            >
                                All Systems ({defaults.length})
                            </button>
                            {availableSystems.map(sys => (
                                <button
                                    key={sys}
                                    onClick={() => setSelectedSystemFilter(sys)}
                                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${selectedSystemFilter === sys ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                    {sys}
                                </button>
                            ))}
                        </div>

                        {!editingItem && (
                            <button
                                onClick={handleNew}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors self-start sm:self-auto shadow-sm"
                            >
                                <span className="material-symbols-outlined text-[15px]">add</span>
                                + Add Entity Default
                            </button>
                        )}
                    </div>

                    {/* Table of Defaults */}
                    <div className="space-y-3">
                        {loading ? (
                            <div className="text-center py-12 text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-2xl mb-2 text-blue-400">sync</span>
                                <p>Loading entity defaults from Sync.EntityTableDefaults...</p>
                            </div>
                        ) : filteredDefaults.length === 0 ? (
                            <div className="text-center py-10 bg-slate-800/40 rounded-xl border border-slate-700 text-slate-400 text-xs">
                                No entity defaults found for selected filter ({selectedSystemFilter}). Click "+ Add Entity Default" above.
                            </div>
                        ) : (
                            <div className="border border-slate-700/80 rounded-xl overflow-hidden bg-slate-800/60 shadow-inner">
                                <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-850 text-slate-400 border-b border-slate-700">
                                            <th className="p-3 font-semibold">Source System</th>
                                            <th className="p-3 font-semibold">Entity Type</th>
                                            <th className="p-3 font-semibold">Root Table</th>
                                            <th className="p-3 font-semibold">QB Destination</th>
                                            <th className="p-3 font-semibold">Description</th>
                                            <th className="p-3 font-semibold text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/60">
                                        {filteredDefaults.map(item => (
                                            <tr key={item.EntityDefaultID || `${item.SourceSystem}_${item.EntityType}`} className="hover:bg-slate-750 transition-colors">
                                                <td className="p-3 font-semibold">
                                                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${item.SourceSystem === 'Passport' ? 'bg-indigo-950 text-indigo-300 border border-indigo-800' : 'bg-purple-950 text-purple-300 border border-purple-800'}`}>
                                                        {item.SourceSystem || 'Passport'}
                                                    </span>
                                                </td>
                                                <td className="p-3 font-bold text-white flex items-center gap-1.5">
                                                    <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                                    {item.EntityType}
                                                </td>
                                                <td className="p-3 font-mono text-emerald-300 font-bold">
                                                    {item.DefaultRootTable}
                                                </td>
                                                <td className="p-3 font-mono text-blue-300">
                                                    {item.DefaultQBEntity}
                                                </td>
                                                <td className="p-3 text-slate-300 max-w-xs truncate">
                                                    {item.Description || '-'}
                                                </td>
                                                <td className="p-3 text-right">
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs font-medium transition-colors"
                                                        >
                                                            Edit
                                                        </button>
                                                        {item.EntityDefaultID && (
                                                            <button
                                                                onClick={() => handleDelete(item)}
                                                                className="px-2 py-1 bg-red-900/30 hover:bg-red-800/60 text-red-300 rounded text-xs font-medium transition-colors"
                                                                title="Delete entity default"
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">delete</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Edit / New Form */}
                    {editingItem && (
                        <form onSubmit={handleSave} className="bg-slate-850 border border-blue-500/40 rounded-xl p-5 space-y-4 shadow-xl">
                            <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                                <h4 className="text-sm font-bold text-blue-400 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">edit_note</span>
                                    {editingItem === 'NEW' ? 'Create New Entity Default' : `Edit Entity: ${form.sourceSystem} / ${form.entityType}`}
                                </h4>
                                <button type="button" onClick={handleCancelEdit} className="text-slate-400 hover:text-white text-xs">
                                    Cancel
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                                <div>
                                    <label className="block text-slate-400 mb-1 font-semibold">Source System</label>
                                    {!customSystemMode ? (
                                        <div className="flex gap-1.5">
                                            <select
                                                value={form.sourceSystem}
                                                onChange={e => {
                                                    if (e.target.value === '__NEW__') {
                                                        setCustomSystemMode(true);
                                                        setForm({ ...form, sourceSystem: '' });
                                                    } else {
                                                        setForm({ ...form, sourceSystem: e.target.value });
                                                    }
                                                }}
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                            >
                                                {availableSystems.map(s => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                                <option value="__NEW__">+ Add Custom System...</option>
                                            </select>
                                        </div>
                                    ) : (
                                        <div className="flex gap-1.5">
                                            <input
                                                type="text"
                                                required
                                                value={form.sourceSystem}
                                                onChange={e => setForm({ ...form, sourceSystem: e.target.value })}
                                                placeholder="e.g. Shopify, CustomERP"
                                                className="w-full bg-slate-900 border border-indigo-500 rounded-lg p-2 text-white"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCustomSystemMode(false);
                                                    setForm({ ...form, sourceSystem: 'Passport' });
                                                }}
                                                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[11px]"
                                            >
                                                Reset
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-slate-400 mb-1 font-semibold">Entity Type</label>
                                    <input
                                        type="text"
                                        required
                                        disabled={editingItem !== 'NEW'}
                                        value={form.entityType}
                                        onChange={e => setForm({ ...form, entityType: e.target.value })}
                                        placeholder="e.g. Customers, Items, Orders"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white disabled:opacity-50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 mb-1 font-semibold">Default Root Table (No Suffix)</label>
                                    <input
                                        type="text"
                                        required
                                        value={form.defaultRootTable}
                                        onChange={e => setForm({ ...form, defaultRootTable: e.target.value.toUpperCase() })}
                                        placeholder="e.g. CUSFIL, ITMFIL, CHARTF"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white font-mono uppercase"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 mb-1 font-semibold">Target QuickBooks Destination</label>
                                    <select
                                        value={form.defaultQBEntity}
                                        onChange={e => setForm({ ...form, defaultQBEntity: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                    >
                                        {qbEntities.map(ent => (
                                            <option key={ent} value={ent}>{ent}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="text-xs">
                                <label className="block text-slate-400 mb-1 font-semibold">Functional Description / Notes</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                    placeholder="e.g. Passport Accounts Receivable Customer Master (CUSFILxx)"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="px-3.5 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-sm"
                                >
                                    {saving ? (
                                        <>
                                            <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-[14px]">save</span>
                                            Save Entity Default
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-slate-800 bg-slate-850 flex justify-between items-center text-xs text-slate-400">
                    <div>
                        Stored in SQL database table <code>Sync.EntityTableDefaults</code>.
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg font-semibold transition-colors border border-slate-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

